import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { updatePassword } from "@aws-amplify/auth";

// Mínimo de la política del user pool (Cognito la vuelve a validar al
// cambiarla; esto solo evita un viaje inútil y da un mensaje claro).
const MIN_LENGTH = 8;

// Errores de Cognito -> mensaje para el usuario.
const ERROR_MESSAGES = {
  NotAuthorizedException: "La contraseña actual es incorrecta.",
  InvalidPasswordException: `La nueva contraseña no cumple la política de seguridad (mínimo ${MIN_LENGTH} caracteres).`,
  LimitExceededException: "Demasiados intentos. Espera unos minutos y vuelve a intentar.",
  TooManyRequestsException: "Demasiados intentos. Espera unos minutos y vuelve a intentar.",
};

const fieldSx = {
  mb: 2,
  input: { color: "var(--wb-text-strong)" },
  "& .MuiInputLabel-root": { color: "var(--wb-text-soft)" },
  "& .MuiOutlinedInput-root fieldset": { borderColor: "var(--wb-outline)" },
};

// Cambio de contraseña del usuario autenticado, 100% client-side (Amplify
// Auth v6 contra Cognito, mismo criterio que TotpEnrollmentForm.js): Cognito
// exige la contraseña actual, así que no hace falta ninguna Lambda propia.
// Las sesiones abiertas en otros dispositivos siguen vigentes hasta que
// venzan sus tokens.
const ChangePasswordForm = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShow(false);
    setError("");
  };

  const validate = () => {
    if (!current || !next || !confirm) return "Completa los tres campos.";
    if (next.length < MIN_LENGTH) return `La nueva contraseña debe tener al menos ${MIN_LENGTH} caracteres.`;
    if (next !== confirm) return "La confirmación no coincide con la nueva contraseña.";
    if (next === current) return "La nueva contraseña debe ser distinta de la actual.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    setError(validationError);
    if (validationError) return;

    setLoading(true);
    try {
      await updatePassword({ oldPassword: current, newPassword: next });
      reset();
      setOpen(false);
      setSuccess("Tu contraseña se actualizó correctamente.");
    } catch (err) {
      console.error("No se pudo cambiar la contraseña", err);
      setError(ERROR_MESSAGES[err?.name] || err?.message || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton
        aria-label={show ? "Ocultar contraseñas" : "Mostrar contraseñas"}
        onClick={() => setShow((s) => !s)}
        edge="end"
        sx={{ color: "gray" }}
      >
        {show ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  if (!open) {
    return (
      <>
        {success && (
          <Typography variant="body2" sx={{ color: "limegreen", mb: 2 }}>
            ✔️ {success}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          onClick={() => {
            setSuccess("");
            setOpen(true);
          }}
        >
          Cambiar Contraseña
        </Button>
      </>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <TextField
        label="Contraseña actual"
        type={show ? "text" : "password"}
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        fullWidth
        size="small"
        sx={fieldSx}
        InputProps={{ endAdornment: visibilityToggle }}
      />
      <TextField
        label="Nueva contraseña"
        type={show ? "text" : "password"}
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        fullWidth
        size="small"
        sx={fieldSx}
        helperText={`Mínimo ${MIN_LENGTH} caracteres.`}
        FormHelperTextProps={{ sx: { color: "gray" } }}
      />
      <TextField
        label="Confirmar nueva contraseña"
        type={show ? "text" : "password"}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        fullWidth
        size="small"
        sx={fieldSx}
      />
      {error && (
        <Typography variant="body2" sx={{ color: "var(--wb-danger)", mb: 2 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          type="button"
          variant="outlined"
          disabled={loading}
          onClick={() => {
            reset();
            setOpen(false);
          }}
          sx={{ flex: 1, borderRadius: 2, textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ flex: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </Box>
    </Box>
  );
};

export default ChangePasswordForm;
